import styled from "styled-components";
import { Link } from "react-router-dom";
import { theme } from "styles/theme";

export const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow-x: hidden;
`;

export const SkipToContent = styled.a`
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 1000;
  padding: 0.75rem 1.25rem;
  background: ${theme.colors.primary};
  color: ${theme.colors.onPrimary};
  font-weight: ${theme.fontWeights.bold};
  border-radius: ${theme.borderRadius.small};
  text-decoration: none;

  &:focus {
    left: 1rem;
    top: 1rem;
  }
`;

export const Header = styled.header`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 1rem;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${theme.breakpoints.tablet}) {
    flex: 1;
  }
`;

export const Logo = styled.img`
  height: 30px;
  margin-right: 20px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-right: 10px;
  }
`;

export const AppTitle = styled.h1`
  margin: 0;
  font-size: ${theme.fontSizes.large};

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const UATBadge = styled.span`
  background-color: ${theme.colors.primary};
  color: ${theme.colors.secondary};
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: ${theme.fontWeights.bold};
  margin-left: 10px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-left: 0;
    font-size: 12px;
  }
`;

export const UserSection = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const UserInfo = styled.div`
  margin-right: 1rem;
`;

export const UserName = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover,
  &:focus-visible {
    opacity: 0.9;
  }
`;

export const LogoutButton = styled.button`
  background-color: transparent;
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.small};
  padding: 0.5rem 1rem;
  font-size: ${theme.fontSizes.small};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.secondary};
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  overflow-x: hidden;
`;

export const Sidebar = styled.aside<{
  $collapsed: boolean;
  $mobileOpen: boolean;
}>`
  width: ${(props) => (props.$collapsed ? "70px" : "250px")};

  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  padding: 1.5rem 0;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
  z-index: 5;
  transition: width 0.3s ease, transform 0.3s ease;
  overflow: hidden;
  overflow-x: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:hover {
    width: ${(props) => (props.$collapsed ? "250px" : "")};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 250px;
    z-index: 5;
    transform: translateX(${(props) => (props.$mobileOpen ? "0" : "-100%")});
    padding-top: 3.5rem;

    &:hover {
      width: 250px;
    }
  }
`;

export const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto; /* Permitir rolagem se houver muitos itens */
  padding-top: 1rem;
`;

export const SidebarItem = styled(Link)<{
  $active?: boolean;
  $collapsed: boolean;
}>`
  padding: 0.8rem 1.5rem;
  color: ${(props) =>
    props.$active ? theme.colors.primary : theme.colors.white};
  text-decoration: none;
  font-weight: ${(props) =>
    props.$active ? theme.fontWeights.bold : theme.fontWeights.normal};
  transition: all 0.2s;
  border-left: 4px solid
    ${(props) => (props.$active ? theme.colors.primary : "transparent")};
  display: flex;
  align-items: center;
  white-space: nowrap;

  .item-icon {
    margin-right: 10px;
    font-size: 1.2rem;
    min-width: 20px;
  }

  .item-text {
    opacity: ${(props) => (props.$collapsed ? "0" : "1")};
    transition: opacity 0.2s ease;
    font-size: 1rem;

    ${Sidebar}:hover & {
      opacity: 1;
    }

    @media (max-width: ${theme.breakpoints.tablet}) {
      opacity: 1;
      font-size: 1.1rem; /* Aumentar o tamanho da fonte para mobile */
    }
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: ${theme.colors.primary};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 1rem 1.5rem; /* Aumentar o padding para facilitar o toque */
    margin-bottom: 0.5rem; /* Adicionar espaço entre os itens */
  }
`;

export const HamburgerButton = styled.button`
  display: none;
  background-color: transparent;
  color: ${theme.colors.primary};
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
  }
`;

export const CloseSidebarButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: transparent;
  color: ${theme.colors.primary};
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 10;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const Overlay = styled.div<{ $visible: boolean }>`
  display: ${(props) => (props.$visible ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 4;
  cursor: pointer;
`;

export const ToggleButton = styled.button<{ $isCollapsed: boolean }>`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.onPrimary};
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease;
  z-index: 10;
  flex-shrink: 0;

  &:hover {
    background-color: ${theme.colors.primaryDark};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.white};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const MainContent = styled.main`
  flex: 1;
  padding: 1rem 2rem;
  background-color: ${theme.colors.grey};
  min-height: calc(100vh - 80px);
  overflow-y: auto;
  overflow-x: hidden;

  > * {
    animation: mainContentEnter 220ms ease;
  }

  @keyframes mainContentEnter {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    > * {
      animation: none;
    }
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 1rem;
  }
`;

export const Footer = styled.footer`
  background-color: ${theme.colors.secondary};
  color: #e5e5e5;
  padding: 1rem 2rem;
  text-align: center;
  font-size: ${theme.fontSizes.small};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 1rem;
  }
`;

export const MobileFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const MobileLogoutButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: transparent;
  color: ${theme.colors.primary};
  border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.small};
  font-size: ${theme.fontSizes.small};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.secondary};
  }

  .logout-icon {
    font-size: 1rem;
  }
`;

export const ThemeToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  margin-right: 0.5rem;
  padding: 0;
  background-color: transparent;
  color: ${theme.colors.primary};
  border: none;
  border-radius: ${theme.borderRadius.small};
  cursor: pointer;
  transition: color 0.2s, opacity 0.2s;
  flex-shrink: 0;

  &:hover {
    opacity: 0.85;
    color: ${theme.colors.primaryLight};
  }

  .pi {
    font-size: 1.15rem;
    line-height: 1;
  }
`;

export const ThemeToggleMobileButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: transparent;
  color: ${theme.colors.primary};
  border: none;
  border-radius: ${theme.borderRadius.small};
  font-size: ${theme.fontSizes.small};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  .pi {
    font-size: 1.15rem;
  }
`;
